import { FormControl, FormGroup } from '@angular/forms';
import { ElementValidationResult, LevelDesignerElementBase } from './element-base';
import { ParkingSpaceElement } from './parking/parkingspace';
import { ParkingLevelElement } from './parking/parkinglevel';

export abstract class PolygonElement extends LevelDesignerElementBase {
    protected points: { x: number, y: number }[];
    protected OutlineColor: string = '#000000';
    protected FillColor: string = 'rgba(0, 0, 0, 0.1)';
    private draggedPointIndex: number | null = null;
    private midpoints: { x: number; y: number }[] = [];
    public elements: LevelDesignerElementBase[] = [];

    constructor(formGroup: FormGroup) {
        super(formGroup);
        
        if (!formGroup.contains('PolygonPoints')) {
            formGroup.addControl('PolygonPoints', new FormControl([[0,0],[0.01,0],[0.01,0.01],[0,0.01]])); 
        }
        else if (formGroup.get('PolygonPoints')?.value?.length === 0) {
            formGroup.patchValue({
                PolygonPoints: [[0,0],[0.01,0],[0.01,0.01],[0,0.01]]
            });
        }

        const pointsInKm : number[][] = this.formGroup.get('PolygonPoints')?.value;
        if(pointsInKm != null){
            // Convert km to m and create objects with x and y properties
            this.points = pointsInKm.map(point => ({
                x: point[0] * 1000, // Convert km to m
                y: point[1] * 1000  // Convert km to m
            }));
            // Remove duplicate end points
            while (this.points.length > 1 && 
                (this.arePointsEqual(this.points[this.points.length - 1], this.points[this.points.length - 2]) ||
                    this.arePointsEqual(this.points[this.points.length - 1], this.points[0]))) {
                this.points.pop();
            }
        }
        else{
            this.points = [];
        }
    }

    private arePointsEqual(p1: { x: number, y: number }, p2: { x: number, y: number }): boolean {
        return p1.x === p2.x && p1.y === p2.y;
    }

    override draw(ctx: CanvasRenderingContext2D, offsetX?: number, offsetY?: number): void {
        // Draw polygon    draw(ctx: CanvasRenderingContext2D, offsetX?: number, offsetY?: number): void {
        const scale = ctx.getTransform().a;  // Get the current scale
        const lineWidth = 2 / scale;  // Adjust line width based on scale

        if(this.points.length == 0) return;

        ctx.fillStyle = this.formGroup.get('FillColor')?.value ?? this.FillColor;
        ctx.strokeStyle = this.formGroup.get('OutlineColor')?.value ?? this.OutlineColor;
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(this.points[0].x + (offsetX??0), this.points[0].y + (offsetY??0));
        for (let i = 1; i < this.points.length; i++) {
            ctx.lineTo(this.points[i].x + (offsetX??0), this.points[i].y + (offsetY??0));
        }
        ctx.closePath();

        
        ctx.fill();
        ctx.stroke();
        ctx.lineWidth = lineWidth;
        // Draw name if it exists
        const name = this.formGroup.get('Name')?.value;
        if (name) {
            // Find the top-most line segment
            let topSegment = { start: this.points[0], end: this.points[1] };
            let minY = Math.min(this.points[0].y, this.points[1].y);

            for (let i = 1; i < this.points.length; i++) {
                const current = this.points[i];
                const next = this.points[(i + 1) % this.points.length];
                const segmentMinY = Math.min(current.y, next.y);
                
                if (segmentMinY < minY) {
                    minY = segmentMinY;
                    topSegment = { start: current, end: next };
                }
            }

            // Calculate text angle and position
            const angle = Math.atan2(
                topSegment.end.y - topSegment.start.y,
                topSegment.end.x - topSegment.start.x
            );
            
            // Position text inside the polygon, below the top line
            const midX = (topSegment.start.x + topSegment.end.x) / 2 + (offsetX??0);
            const midY = (topSegment.start.y + topSegment.end.y) / 2 + (offsetY??0);
            
            ctx.save();
            ctx.translate(midX, midY);
            ctx.rotate(angle);
            
            // Draw text
            ctx.fillStyle = '#000000';
            ctx.font = `${0.3 + 0.1 * Math.log2(scale)}px Arial`; // Base size + logarithmic scaling
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(name, 0, 2/scale); // Small offset scaled with zoom
            
            ctx.restore();
        }
    }

    public drawGeo(ctx: CanvasRenderingContext2D): void {
        // Draw a polygon with appropriate styling for map mode
        if (this.points.length < 2) return;
        
        ctx.save();
        
        // Set up styling based on element type
        if (this instanceof ParkingLevelElement) {
            ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
            ctx.fillStyle = 'rgba(255, 0, 0, 0.1)';
            ctx.lineWidth = 2;
        } else if (this.TypeName === "ParkingRow") {
            ctx.strokeStyle = 'rgba(0, 0, 255, 0.8)';
            ctx.fillStyle = 'rgba(0, 0, 255, 0.1)';
            ctx.lineWidth = 1.5;
        } else if (this.TypeName === "ParkingSpace") {
            ctx.strokeStyle = 'rgba(0, 128, 0, 0.8)';
            ctx.fillStyle = 'rgba(0, 128, 0, 0.2)';
            ctx.lineWidth = 1;
        } else {
            ctx.strokeStyle = 'rgba(128, 128, 128, 0.8)';
            ctx.fillStyle = 'rgba(128, 128, 128, 0.2)';
            ctx.lineWidth = 1;
        }
        
        // Draw the polygon
        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.points[0].y);
        
        for (let i = 1; i < this.points.length; i++) {
            ctx.lineTo(this.points[i].x, this.points[i].y);
        }
        
        // Close the path
        ctx.lineTo(this.points[0].x, this.points[0].y);
        
        // Fill and stroke the polygon
        ctx.fill();
        ctx.stroke();
        
        ctx.restore();
    }

    

    protected rotateImplementation(angle: number, centerX: number, centerY: number): void {
        // Convert angle from degrees to radians
        angle = angle * Math.PI / 180;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        this.points = this.points.map(point => {
            const dx = point.x - centerX;
            const dy = point.y - centerY;
            return {
                x: centerX + dx * cos - dy * sin,
                y: centerY + dx * sin + dy * cos
            };
        });
    }

    isPointInside(x: number, y: number): boolean {
        let inside = false;
        for (let i = 0, j = this.points.length - 1; i < this.points.length; j = i++) {
            const xi = this.points[i].x, yi = this.points[i].y;
            const xj = this.points[j].x, yj = this.points[j].y;
            
            const intersect = ((yi > y) !== (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    }

    move(deltaX: number, deltaY: number): void {
        this.points = this.points.map(point => ({
            x: point.x + deltaX,
            y: point.y + deltaY
        }));
    }

    getBoundingBox(): { x: number, y: number, width: number, height: number } {
        const xs = this.points.map(p => p.x);
        const ys = this.points.map(p => p.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
        };
    }

    toJSON(): object {
        return {
            type: 'polygon',
            points: this.points.map(point => [point.x / 1000, point.y / 1000]), // Convert m to km
            strokeColor: this.OutlineColor,
            fillColor: this.FillColor
        };
    }

    fromJSON(json: any): void {
        if (json.type === 'polygon') {
            this.points = json.points.map((point: number[]) => ({
                x: point[0] * 1000, // Convert km to m
                y: point[1] * 1000  // Convert km to m
            }));
            this.OutlineColor = json.strokeColor;
            this.FillColor = json.fillColor;
        }
    }

    isPointNearVertex(x: number, y: number, threshold: number = 10): number {
        if (!this.selected) return -1;
        
        for (let i = 0; i < this.points.length; i++) {
            const dx = this.points[i].x - x;
            const dy = this.points[i].y - y;
            if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
                return i;
            }
        }
        return -1;
    }
    
    
    isPointNearMidpoint(x: number, y: number): number {
        const threshold = 5; // Adjust this value to change the click sensitivity
        for (let i = 0; i < this.midpoints.length; i++) {
        const dx = this.midpoints[i].x - x;
        const dy = this.midpoints[i].y - y;
        if (dx * dx + dy * dy <= threshold * threshold) {
            return i;
        }
        }
        return -1;
    }

    insertPointAtMidpoint(index: number): void {
        const nextIndex = (index + 1) % this.points.length;
        const newPoint = {
          x: (this.points[index].x + this.points[nextIndex].x) / 2,
          y: (this.points[index].y + this.points[nextIndex].y) / 2
        };
        this.points.splice(nextIndex, 0, newPoint);
        this.calculateMidpoints();
    }

    private calculateMidpoints(): void {
        this.midpoints = [];
        for (let i = 0; i < this.points.length; i++) {
            const nextIndex = (i + 1) % this.points.length;
            const midX = (this.points[i].x + this.points[nextIndex].x) / 2;
            const midY = (this.points[i].y + this.points[nextIndex].y) / 2;
            this.midpoints.push({ x: midX, y: midY });
        }
    }

    startDraggingPoint(index: number): void {
        this.draggedPointIndex = index;
    }

    stopDraggingPoint(): void {
        this.draggedPointIndex = null;
    }

    dragPoint(deltaX: number, deltaY: number): void {
        if (this.draggedPointIndex !== null) {
            this.points[this.draggedPointIndex].x += deltaX;
            this.points[this.draggedPointIndex].y += deltaY;
        }
    }

    protected scaleImplementation(scaleX: number, scaleY: number, anchorX: number, anchorY: number): void {
        this.points = this.points.map(point => ({
            x: anchorX + (point.x - anchorX) * scaleX,
            y: anchorY + (point.y - anchorY) * scaleY
        }));
    }

    // Add this method to the PolygonElement class
    public override scale(scaleX: number, scaleY: number, anchorX: number, anchorY: number): void {
        this.points = this.points.map(point => ({
            x: anchorX + (point.x - anchorX) * scaleX,
            y: anchorY + (point.y - anchorY) * scaleY
        }));
    }

    // Add this method to the PolygonElement class
    public scaleFrom(original: PolygonElement, scaleX: number, scaleY: number, anchorX: number, anchorY: number): void {
        this.points = original.points.map(point => ({
            x: anchorX + (point.x - anchorX) * scaleX,
            y: anchorY + (point.y - anchorY) * scaleY
        }));
    }

    public getLineSegmentNearPoint(x: number, y: number, threshold: number = 5): number {
        for (let i = 0; i < this.points.length; i++) {
            const nextIndex = (i + 1) % this.points.length;
            const p1 = this.points[i];
            const p2 = this.points[nextIndex];

            const distanceToLine = this.distanceToLineSegment(x, y, p1.x, p1.y, p2.x, p2.y);
            if (distanceToLine <= threshold) {
                return i;
            }
        }
        return -1;
    }

    private distanceToLineSegment(x: number, y: number, x1: number, y1: number, x2: number, y2: number): number {
        const A = x - x1;
        const B = y - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        if (lenSq !== 0) {
            param = dot / lenSq;
        }

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = x - xx;
        const dy = y - yy;
        return Math.sqrt(dx * dx + dy * dy);
    }

    public resizeLineSegment(lineIndex: number, distance: number): void {
        console.log('PolygonElement: resizeLineSegment called');
        const nextIndex = (lineIndex + 1) % this.points.length;
        const p1 = this.points[lineIndex];
        const p2 = this.points[nextIndex];

        // Calculate the direction vector of the line segment
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const length = Math.sqrt(dx * dx + dy * dy);

        // Normalize the direction vector
        const unitDx = dx / length;
        const unitDy = dy / length;

        // Calculate the perpendicular vector
        const perpDx = -unitDy;
        const perpDy = unitDx;

        // Move both points of the line segment
        this.points[lineIndex].x += perpDx * distance;
        this.points[lineIndex].y += perpDy * distance;
        this.points[nextIndex].x += perpDx * distance;
        this.points[nextIndex].y += perpDy * distance;

        // Recalculate midpoints after resizing
        this.calculateMidpoints();

        // Update form after resizing
        this.updateForm();
    }

    // Add this new method
    public getPoints(): { x: number, y: number }[] {
        return this.points;
    }

    public getLineSegments(): { x1: number, y1: number, x2: number, y2: number }[] {
        const segments: { x1: number, y1: number, x2: number, y2: number }[] = [];
        
        for (let i = 0; i < this.points.length; i++) {
            const nextIndex = (i + 1) % this.points.length;
            segments.push({
                x1: this.points[i].x,
                y1: this.points[i].y,
                x2: this.points[nextIndex].x,
                y2: this.points[nextIndex].y
            });
        }
        
        return segments;
    }

    protected doLineSegmentsIntersect(
        x1: number, y1: number, x2: number, y2: number,
        x3: number, y3: number, x4: number, y4: number
    ): boolean {
        // Calculate the direction of the lines
        const d1 = this.direction(x3, y3, x4, y4, x1, y1);
        const d2 = this.direction(x3, y3, x4, y4, x2, y2);
        const d3 = this.direction(x1, y1, x2, y2, x3, y3);
        const d4 = this.direction(x1, y1, x2, y2, x4, y4);

        // Check if lines intersect
        if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
            ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
            return true;
        }

        // Check if lines are collinear and overlapping
        if (d1 === 0 && this.onSegment(x3, y3, x1, y1, x4, y4)) return true;
        if (d2 === 0 && this.onSegment(x3, y3, x2, y2, x4, y4)) return true;
        if (d3 === 0 && this.onSegment(x1, y1, x3, y3, x2, y2)) return true;
        if (d4 === 0 && this.onSegment(x1, y1, x4, y4, x2, y2)) return true;

        return false;
    }

    private direction(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): number {
        return ((x2 - x1) * (y3 - y1)) - ((x3 - x1) * (y2 - y1));
    }

    private onSegment(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): boolean {
        return x2 <= Math.max(x1, x3) && x2 >= Math.min(x1, x3) &&
               y2 <= Math.max(y1, y3) && y2 >= Math.min(y1, y3);
    }

    public override updateForm(): void {
        // Convert points from meters to kilometers for storage
        const pointsInKm = this.points.map(point => [
            point.x / 1000, // Convert m to km
            point.y / 1000  // Convert m to km
        ]);
        
        // Update the form control
        let control = this.formGroup.get('PolygonPoints');
        if (!control) {
          
            control = new FormControl<number[][]>(pointsInKm);
            this.formGroup.addControl('PolygonPoints', control);
        }
        if (control) {
            const currentValue = control.value;
            // Only update and mark dirty if points have actually changed
            if (JSON.stringify(currentValue) !== JSON.stringify(pointsInKm)) {
                control.setValue(pointsInKm);
                control.markAsDirty(); // Only mark dirty when points change
                console.log('PolygonElement: Form marked as dirty due to point changes');
            }
        }
    }

    public translate(dx: number, dy: number): void {
        // Move all points by the specified offset
        this.points = this.points.map(point => ({
            x: point.x + dx,
            y: point.y + dy
        }));

        // Update the form values
        this.updateForm();
    }

    public removePoint(index: number): void {
        this.points.splice(index, 1);
        this.formGroup.markAsDirty();
    }

    protected override isInsidePolygon(polygonElement: LevelDesignerElementBase): boolean {
        return this.points.every(point => polygonElement.isPointInside(point.x, point.y));
    }

    
}
